
import React, { createContext, useContext } from 'react';

// Define the shape of the language context
interface LanguageContextProps {
  locale: string;
  language: string; 
  t: (key: string, args?: Record<string, any>) => string;
  setLocale: (locale: string) => void;
  setLanguage: (locale: string) => void; 
}

// Create the language context with a default value - now defaulting to Greek
const LanguageContext = createContext<LanguageContextProps>({
  locale: 'el',
  language: 'el', 
  t: (key: string) => key, 
  setLocale: () => {}, 
  setLanguage: () => {}, 
});

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Define the translation messages for different languages
export const translations = {
  en: {
    // App basics
    appName: "Scheduling App",
    welcome: "Welcome",
    toCentralPanel: "to OVIT Central Panel",
    dashboardOverview: "Dashboard Overview",
    welcomeToDashboard: "Welcome to your security management dashboard",
    
    // Navigation & Menus
    guardManagement: "Guard Management",
    guardManagementDesc: "Manage your security personnel efficiently",
    performanceEvaluation: "Performance Evaluation",
    performanceEvaluationDesc: "Monitor and evaluate performance",
    clientsManagement: "Clients Management",
    clientsManagementDesc: "Manage your clients effectively",
    teamOverview: "Team Overview",
    teamOverviewDesc: "Monitor and organize your security teams",
    reportsSubmissions: "Reports & Submissions",
    reportsSubmissionsDesc: "Track and analyze security incidents",
    workInstructions: "Work Instructions",
    workInstructionsDesc: "View and manage team work instructions",
    workApplications: "Work Applications",
    workApplicationsDesc: "Manage team applications and resources",
    mySchedule: "My Schedule",
    myScheduleDesc: "Organize guard shifts and rotations",
    "My Teams Schedule": "My Teams Schedule",
    "My Teams Schedule Description": "View your assigned teams and their schedules",
    teamsScheduling: "Teams Scheduling",
    teamsSchedulingDesc: "View and manage team scheduling for clients",
    incidentAlerts: "Incident Alerts",
    incidentAlertsDesc: "Real-time security incident notifications",
    adminTeamMembers: "Admin Team Members",
    adminTeamMembersDesc: "Manage Administration team members",
    performanceMetrics: "Performance Metrics",
    performanceMetricsDesc: "Monitor key security metrics",
    
    // Language & Theme
    new: "New",
    newAlertIn: "New alert in",
    greek: "Ελληνικά",
    english: "English",
    french: "Français",
    darkMode: "Dark Mode",
    lightMode: "Light Mode",
    language: "Language",
    
    // Common UI Elements
    calendarSize: "Calendar Size",
    small: "Small",
    large: "Large",
    colorIntensity: "Color Intensity",
    muted: "Muted",
    vibrant: "Vibrant",
    noShifts: "No shifts",
    
    // User Roles
    superAdmin: "Super Admin",
    contributor: "Contributor",
    admin: "Admin",
    manager: "Manager",
    guard: "Guard",
    client: "Client",
    user: "User",
    
    // Sidebar Menu Items
    dashboard: "Dashboard",
    hrManagement: "HR Management",
    demographics: "Demographics",
    leaveRequests: "Leave Requests",
    guardsMenu: "Guards",
    teamsMenu: "Teams",
    scheduling: "Scheduling",
    scheduleLogs: "Schedule Logs",
    reportsMenu: "Reports",
    sentinel: "Sentinel",
    settings: "Settings",
    userCredentials: "User Credentials",
    documentation: "Documentation",
    
    // User Credentials Page
    userCredentialsPage: "User Credentials",
    managePasswords: "Manage user and client passwords securely",
    administratorPanel: "Administrator Panel",
    totalAccounts: "Total Accounts",
    systemUsers: "System Users",
    clients: "Clients",
    searchUsers: "Search users by name, email, or notes...",
    allTypes: "All Types",
    foundResults: "Found",
    resultsFor: "result(s) for",
    loadingCredentials: "Loading credentials...",
    noCredentialsFound: "No credentials found",
    noResultsFor: "No results for",
    tryAdjustingFilters: "Try adjusting your filters",
    systemUser: "User",
    created: "Created",
    updated: "Updated",
    changePassword: "Change Password",
    newPassword: "New Password",
    enterNewPassword: "Enter new password (min 6 characters)",
    passwordMinLength: "Password must be at least 6 characters long",
    notesOptional: "Notes (Optional)",
    addNotesAboutChange: "Add any notes about this password change...",
    updating: "Updating...",
    updatePassword: "Update Password",
    passwordUpdated: "Password Updated",
    passwordUpdatedSuccess: "password has been successfully updated.",
    failedToUpdatePassword: "Failed to update password. Please try again.",
    pleaseEnterPassword: "Please enter a new password",
    accessDenied: "Access Denied",
    restrictedToAdmins: "This area is restricted to administrators only",
    
    // Dashboard/Index Page
    systemActive: "System Active",
    featuresAvailable: "Features Available",
    guardlySecuritySystem: "Shiftask Security System",
    systemOnline: "System Online",
    
    // Teams Management
    teamManagement: "Team Management",
    manageAllTeams: "Manage all security teams and their assignments",
    viewYourTeams: "View your teams and assignments",
    teamAnalytics: "Team Analytics",
    realTimeMonitoring: "Real-time Monitoring",
    addNewTeam: "Add New Team",
    emailToGuards: "Email to Guards",
    couldNotFetchEmails: "Could not fetch guard emails",
    noGuardsFound: "No guards found",
    noActiveGuardsFound: "No active guards were found in the system",
    noEmailsFound: "No emails found",
    noGuardsWithEmails: "There are no guards with email addresses",
    noTeamsCreated: "No Teams Created",
    noTeamAssignments: "No Team Assignments",
    createFirstTeam: "Create your first team to get started with team management.",
    notMemberOfTeams: "You are not a member of any teams yet. Contact your administrator.",
    administratorAccess: "Administrator Access",
    
    // Applications Management
    viewManageApplications: "View and manage work applications by team",
    createNewApplication: "Create New Work Application",
    errorLoadingTeams: "Error loading teams",
    loadingTeams: "Loading teams...",
    noTeamsAvailable: "No teams available",
    pleaseCreateTeamFirst: "Please create a team first",
    noTeamsFoundCreateFirst: "No teams found. Please create a team first to manage work applications.",
    applications: "applications",
    editTeam: "Edit team",
    noDescriptionAvailable: "No description available",
    deleteTeam: "Delete Team",
    viewApplications: "View Applications",
    makeChangesHere: "Make changes to your team here. Click save when you're done.",
    enterTeamDescription: "Enter team description",
    teamImage: "Team Image",
    newImageSelected: "New image selected:",
    currentImage: "Current image:",
    saving: "Saving...",
    teamUpdated: "Team updated",
    teamUpdatedSuccessfully: "The team has been updated successfully.",
    failedToUpdate: "Failed to update",
    errorUpdatingTeam: "An error occurred while updating the team.",
    
    // 404 Not Found Page
    pageNotFound: "Page not found",
    pageNotFoundMessage: "We've recorded this issue and will look into it.",
    goBack: "Go Back",
    home: "Home",
    allApplications: "All Applications",
    lookingForTeamChat: "Looking for team chat?",
    teamChatNotExistMessage: "It seems you're trying to access a team chat that doesn't exist or you don't have permission to view.",
    path: "Path",
    reference: "Reference",
    
    // Settings Page
    settingsTitle: "Settings",
    personalizeWorkspace: "Personalize your workspace and manage your preferences with our beautiful, intuitive interface",
    navigation: "Navigation",
    general: "General",
    generalDescription: "Basic account and preferences",
    manageYourProfile: "Manage your profile",
    customizeInterface: "Customize your interface",
    controlYourAlerts: "Control your alerts",
    dataManagement: "Data Management",
    dataManagementDescription: "Reports, backups, and integrations",
    configureReportSettings: "Configure report settings",
    manageDataBackups: "Manage data backups",
    externalConnections: "External connections",
    equipmentDescription: "Manage equipment types and assignments",
    guardEquipmentTypes: "Guard Equipment Types",
    guardEquipmentCatalog: "Guard equipment catalog",
    teamEquipmentTypes: "Team Equipment Types",
    teamEquipmentCatalog: "Team equipment catalog",
    assignEquipmentToGuards: "Assign Equipment to Guards",
    guardAssignments: "Guard assignments",
    assignEquipmentToTeams: "Assign Equipment to Teams",
    teamAssignments: "Team assignments",
    financeDescription: "Financial settings and cost management",
    configureHourlyRates: "Configure hourly rates",
    schedulingDescription: "Shift management and scheduling features",
    enableShiftSwapRequests: "Enable shift swap requests",
    systemAdministration: "System Administration",
    systemAdministrationDescription: "Advanced system settings and user management",
    manageUsersAndRoles: "Manage users and roles",
    adminPrivilegesMessage: "You have administrative privileges and can access advanced configuration options.",
    
    // Account Settings
    account: "Account",
    appearance: "Appearance",
    notifications: "Notifications",
    reports: "Reports",
    backup: "Backup",
    integrations: "Integrations",
    equipment: "Equipment",
    finance: "Finance",
    hourlyCosts: "Hourly Costs",
    shiftSwaps: "Shift Swaps",
    userManagement: "User Management",
    adminAccess: "Admin Access",
    accountLimits: "Account Limits",
    yourCurrentAccountUsageLimits: "Your current account usage limits set by the administrator",
    moduleAccess: "Module Access",
    availableAndDisabledModules: "Available and disabled modules for your account (informational only)",
    profileInformation: "Profile Information",
    updateYourPersonalInformation: "Update your personal information and contact details",
    profilePicture: "Profile Picture",
    changePhoto: "Change Photo",
    removePhoto: "Remove Photo",
    recommendedSquareImage: "Recommended: Square image, at least 400x400px",
    basicInformation: "Basic Information",
    emailCannotBeChanged: "Email cannot be changed",
    fullName: "Full Name",
    enterYourFullName: "Enter your full name",
    contactInformation: "Contact Information",
    phone: "Phone",
    enterPhoneNumber: "Enter phone number",
    enterMobilePhoneNumber: "Enter mobile phone number",
    enterYourAddress: "Enter your address",
    emergencyContact: "Emergency Contact",
    emergencyContactNumber: "Emergency contact number",
    additionalInformation: "Additional Information",
    afmTaxId: "VAT (Tax ID)",
    enterAFM: "Enter VAT",
    identityNumber: "Identity Number",
    enterIdentityNumber: "Enter identity number",
    saveChanges: "Save Changes",
    profileUpdatedSuccessfully: "Profile updated successfully",
    avatarUpdatedSuccessfully: "Avatar updated successfully",
    avatarRemovedSuccessfully: "Avatar removed successfully",
    uploadFailed: "Upload failed",
    removeFailed: "Remove failed",
    couldNotUploadAvatar: "Could not upload avatar",
    couldNotRemoveAvatar: "Could not remove avatar",
    errorLoadingProfile: "Error loading profile",
    errorUpdatingProfile: "Error updating profile",
    userNotFound: "User not found",
    failedToLoadProfile: "Failed to load profile",
    failedToUpdateProfile: "Failed to update profile",
    uploading: "Uploading...",
    unlimited: "Unlimited",
    noLimitsHaveBeenSet: "No limits have been set. You have unlimited usage for all resources.",
    enabled: "Enabled",
    disabled: "Disabled",
    firstName: "First Name",
    lastName: "Last Name",
    enterFirstName: "Enter your first name",
    enterLastName: "Enter your last name",
    mobilePhone: "Mobile Phone",
    address: "Address",
    dateOfBirth: "Date of Birth",
    
    // Appearance Settings
    brandingAppearanceCustomization: "Branding & Appearance Customization",
    contributorAppearanceMessage: "As a contributor, your appearance settings are managed by your parent admin account. This ensures consistent branding across your organization!",
    inheritThemeMessage: "You'll automatically inherit the theme, colors, and branding from your admin.",
    
    // Notification Preferences
    manageNotificationPreferences: "Manage how you receive notifications",
    notificationChannels: "Notification Channels",
    notificationTypes: "Notification Types",
    emailNotifications: "Email Notifications",
    chooseEmailNotifications: "Choose which notifications you want to receive via email",
    receiveNotificationsViaEmail: "Receive notifications via email",
    pushNotifications: "Push Notifications",
    receivePushNotifications: "Receive instant notifications in your browser",
    receivePushNotificationsOnDevice: "Receive push notifications on your device",
    securityNotifications: "Security Notifications",
    importantSecurityNotifications: "Important security-related notifications",
    shiftReminders: "Shift Reminders",
    getNotifiedUpcomingShifts: "Get notified about upcoming shifts",
    scheduleChanges: "Schedule Changes",
    notifiedScheduleModified: "Be notified when your schedule is modified",
    notificationsAboutScheduleUpdates: "Notifications about schedule updates",
    emergencyAlerts: "Emergency Alerts",
    criticalAlertsImmediate: "Critical alerts requiring immediate attention",
    loginAlerts: "Login Alerts",
    notifiedLoginAttempts: "Get notified of new login attempts",
    passwordChanges: "Password Changes",
    notificationsPasswordModifications: "Notifications about password modifications",
    securityNotificationsCannotDisabled: "Security notifications cannot be disabled for safety reasons. These include login alerts and password change notifications.",
    mobilePushNotifications: "Mobile Push Notifications",
    receiveNotificationsMobile: "Receive notifications on your mobile device",
    desktopNotifications: "Desktop Notifications",
    browserDesktopNotifications: "Browser desktop notifications",
    soundAlerts: "Sound Alerts",
    playSoundImportant: "Play sound for important notifications",
    noChanges: "No changes",
    preferencesUpdated: "Preferences Updated",
    notificationPreferencesSaved: "Your notification preferences have been saved.",
    smsAlerts: "SMS Alerts",
    receiveCriticalAlertsViaSms: "Receive critical alerts via SMS",
    incidentReports: "Incident Reports",
    notificationsAboutSecurityIncidents: "Notifications about security incidents",
    serviceAccountsActivity: "Service Accounts Activity",
    notificationsAboutServiceAccountUsage: "Notifications about service account usage",
    savePreferences: "Save Preferences",
    
    // Module names
    facilityPresence: "Facility Presence",
    missions: "Missions",
    whistleblowers: "Whistleblowers",
    ethicsCompliance: "Ethics & Compliance",
    equipmentManagement: "Equipment Management",
    supplierEvaluations: "Supplier Evaluations",
    
    // Common UI Elements
    activeUsers: "Active Users",
    archived: "Archived",
    loading: "Loading...",
    users: "users",
    showing: "Showing",
    noDataAvailable: "No data available",
    noResultsFound: "No results found",
    tryAgain: "Try again",
    viewAll: "View all",
    viewDetails: "View details",
    learnMore: "Learn more",
    getStarted: "Get started",
    comingSoon: "Coming soon",
    newFeature: "New feature",
    beta: "Beta",
    
    // Common Table Headers
    id: "ID",
    name: "Name",
    email: "Email",
    date: "Date",
    time: "Time",
    status: "Status",
    actions: "Actions",
    createdAt: "Created at",
    updatedAt: "Updated at",
    
    // Common Form Labels
    username: "Username",
    password: "Password",
    confirmPassword: "Confirm Password",
    phoneNumber: "Phone Number",
    city: "City",
    state: "State",
    country: "Country",
    zipCode: "Zip Code",
    
    // Common Actions Extended
    select: "Select",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    clear: "Clear",
    clearAll: "Clear All",
    apply: "Apply",
    confirm: "Confirm",
    reject: "Reject",
    approve: "Approve",
    deny: "Deny",
    
    // Alerts Page
    alertsDashboard: "Alerts Dashboard",
    loadingAlertsDashboard: "Loading alerts dashboard...",
    emergencyReports: "Emergency Reports",
    patrolObservations: "Patrol Observations",
    viewAllAlerts: "View All Alerts",
    criticalAlerts: "Critical Alerts",
    warningAlerts: "Warning Alerts",
    infoAlerts: "Info Alerts",
    markAsRead: "Mark as Read",
    markAllAsRead: "Mark All as Read",
    clearAlerts: "Clear Alerts",
    alertDetails: "Alert Details",
    alertType: "Alert Type",
    alertTime: "Alert Time",
    alertLocation: "Alert Location",
    resolveAlert: "Resolve Alert",
    escalateAlert: "Escalate Alert",
    
    // Reports Page
    reportsTitle: "Reports",
    generateReport: "Generate Report",
    downloadReport: "Download Report",
    reportType: "Report Type",
    dateRange: "Date Range",
    customReport: "Custom Report",
    monthlyReport: "Monthly Report",
    weeklyReport: "Weekly Report",
    dailyReport: "Daily Report",
    exportToPDF: "Export to PDF",
    exportToExcel: "Export to Excel",
    exportToCSV: "Export to CSV",
    noReportsAvailable: "No reports available",
    generatingReport: "Generating report...",
    reportGenerated: "Report generated successfully",
    incidentReport: "Incident Report",
    activityReport: "Activity Report",
    summaryReport: "Summary Report",
    detailedReport: "Detailed Report",
    
    // Performance Page
    performanceTitle: "Performance",
    kpiDashboard: "KPI Dashboard",
    goals: "Goals",
    objectives: "Objectives",
    evaluations: "Evaluations",
    ratings: "Ratings",
    feedback: "Feedback",
    improvements: "Improvements",
    achievements: "Achievements",
    performanceScore: "Performance Score",
    targetMet: "Target Met",
    targetMissed: "Target Missed",
    onTrack: "On Track",
    needsImprovement: "Needs Improvement",
    exceeding: "Exceeding",
    
    // Clients Page
    clientsTitle: "Clients",
    clientManagement: "Client Management",
    addClient: "Add Client",
    editClient: "Edit Client",
    deleteClient: "Delete Client",
    clientDetails: "Client Details",
    contractDetails: "Contract Details",
    serviceAgreements: "Service Agreements",
    billingInfo: "Billing Information",
    contactPerson: "Contact Person",
    companyName: "Company Name",
    vatNumber: "VAT Number",
    contractStartDate: "Contract Start Date",
    contractEndDate: "Contract End Date",
    activeContracts: "Active Contracts",
    expiredContracts: "Expired Contracts",
    
    // Missions Page
    missionsTitle: "Missions",
    activeMissions: "Active Missions",
    completedMissions: "Completed Missions",
    upcomingMissions: "Upcoming Missions",
    missionDetails: "Mission Details",
    assignTeam: "Assign Team",
    missionStatus: "Mission Status",
    priority: "Priority",
    high: "High",
    medium: "Medium",
    low: "Low",
    urgent: "Urgent",
    missionBriefing: "Mission Briefing",
    missionDebriefing: "Mission Debriefing",
    startMission: "Start Mission",
    completeMission: "Complete Mission",
    abortMission: "Abort Mission",
    
    // Equipment Management
    equipmentTitle: "Equipment",
    inventoryManagement: "Inventory Management",
    checkIn: "Check In",
    checkOut: "Check Out",
    maintenance: "Maintenance",
    repairs: "Repairs",
    replacement: "Replacement",
    equipmentStatus: "Equipment Status",
    available: "Available",
    inUse: "In Use",
    underMaintenance: "Under Maintenance",
    retired: "Retired",
    damaged: "Damaged",
    lost: "Lost",
    serialNumber: "Serial Number",
    manufacturer: "Manufacturer",
    model: "Model",
    purchaseDate: "Purchase Date",
    warrantyExpiry: "Warranty Expiry",
    lastServiced: "Last Serviced",
    nextServiceDue: "Next Service Due",
    assignedTo: "Assigned To",
    
    // Facility Presence
    facilityPresenceTitle: "Facility Presence",
    checkInOut: "Check In/Out",
    currentlyPresent: "Currently Present",
    lastSeen: "Last Seen",
    timeOnSite: "Time on Site",
    expectedArrival: "Expected Arrival",
    expectedDeparture: "Expected Departure",
    accessPoints: "Access Points",
    securityZones: "Security Zones",
    restrictedAreas: "Restricted Areas",
    
    // Whistleblowers
    whistleblowersTitle: "Whistleblowers",
    submitReport: "Submit Report",
    anonymousReport: "Anonymous Report",
    confidentialReport: "Confidential Report",
    reportStatus: "Report Status",
    underInvestigation: "Under Investigation",
    resolved: "Resolved",
    dismissed: "Dismissed",
    actionTaken: "Action Taken",
    followUp: "Follow Up",
    
    // Guards Management Page
    securityPersonnel: "SECURITY PERSONNEL",
    manageSecurityTeam: "Manage your security team members and their information",
    addNewGuard: "Add New Guard",
    createGuardAccount: "Create a new guard account. Required fields are marked with *.",
    bulkUpload: "Bulk Upload",
    bulkUploadGuards: "Bulk Upload Guards",
    uploadMultipleGuards: "Upload multiple guards at once using an Excel spreadsheet.",
    filterSearchPersonnel: "Filter & Search Personnel",
    teamFilter: "Team",
    roleFilter: "Role",
    noPersonnelFound: "No Personnel Found",
    getStartedAddGuard: "Get started by adding your first guard using the buttons above.",
    noGuardsMatchFilters: "No guards match your current filters.",
    showingGuards: "Showing",
    guardsCount: "guards",
    
    // HR Management Dashboard
    backToDashboard: "Back to Dashboard",
    humanResourcesManagement: "Human Resources Management",
    comprehensiveRecruitment: "Comprehensive recruitment and talent management system",
    activeRecruitment: "Active Recruitment",
    smartAssessment: "Smart Assessment",
    realTimeTracking: "Real-time Tracking",
    downloadHiringProcess: "Download Hiring Process",
    downloadProcess: "Download Process",
    addCandidate: "Add Candidate",
    activeCandidates: "Active Candidates",
    totalInProcess: "Total in process",
    pendingInterviews: "Pending Interviews",
    scheduled: "Scheduled",
    pendingTests: "Pending Tests",
    awaitingResults: "Awaiting results",
    readyToHire: "Ready to Hire",
    completedAllStages: "Completed all stages",
    candidates: "Candidates",
    tests: "Tests",
    workflowTab: "Workflow",
    hired: "Hired",
    hrManagementSystem: "HR Management System",
    
    // Requests
    submitNewRequest: "Submit New Request",
    leaveType: "Leave Type",
    startDate: "Start Date",
    endDate: "End Date",
    pickDate: "Pick a date",
    reasonOptional: "Reason (Optional)",
    leaveRequestInfo: "Please provide any additional information about your leave request",
    authenticationRequired: "Authentication required",
    mustBeLoggedIn: "You must be logged in to submit a leave request",
    success: "Success!",
    leaveRequestSubmitted: "Your leave request has been submitted",
    error: "Error",
    failedToSubmit: "Failed to submit leave request",
    selectLeaveType: "Select a leave type",
    repo: "REPO",
    leave: "Leave",
    other: "Other",
    Requests: "Requests",
    "Submit and manage requests": "Submit and manage requests",
    "All Requests": "All Requests",
    "My Requests": "My Requests",
    "All Leave Requests": "All Leave Requests",
    "Team Leave Requests": "Team Leave Requests",
    "My Leave Requests": "My Leave Requests",
    "Submit New Leave Request": "Submit New Leave Request",
    
    // Training Management
    loadingTrainingModules: "Loading training modules...",
    trainingManagement: "Training Management",
    trainingDashboard: "Training Dashboard",
    trainingModules: "Training Modules",
    trainingProgress: "Training Progress",
    certifications: "Certifications",
    myTraining: "My Training",
    allModules: "All Modules",
    inProgress: "In Progress",
    completed: "Completed",
    notStarted: "Not Started",
    startTraining: "Start Training",
    continueTraining: "Continue Training",
    reviewTraining: "Review Training",
    completionRate: "Completion Rate",
    overallProgress: "Overall Progress",
    difficulty: "Difficulty",
    duration: "Duration",
    minutes: "minutes",
    beginner: "Beginner",
    easy: "Easy",
    intermediate: "Intermediate",
    advanced: "Advanced",
    expert: "Expert",
    certificate: "Certificate",
    certified: "Certified",
    takeQuiz: "Take Quiz",
    retakeQuiz: "Retake Quiz",
    quizResults: "Quiz Results",
    passingScore: "Passing Score",
    yourScore: "Your Score",
    timeSpent: "Time Spent",
    attempts: "Attempts",
    scheduledSessions: "Scheduled Sessions",
    upcomingTraining: "Upcoming Training",
    assignedTraining: "Assigned Training",
    optionalTraining: "Optional Training",
    mandatoryTraining: "Mandatory Training",
    
    // Guard Form Fields
    code: "Code",
    afm: "VAT",
    hiringDate: "Hiring Date",
    licenseExpiryDate: "License Expiry Date",
    teamsOptional: "Teams (Optional)",
    enterEmail: "Enter email address",
    selectTeam: "Select team",
    
    // Schedule and Alerts
    scheduleAlerts: "Schedule Alerts",
    aboutScheduleAlerts: "About Schedule Alerts", 
    scheduleAlertsDescription: "Schedule Alerts Description",
    customDateGuardView: "Custom Date Guard View",
    allGuardsMonthView: "All Guards Month View",
    allGuardsWeekView: "All Guards Week View",
    customEmployeeReport: "Custom Employee Report",
    scheduleDescription: "Schedule Description",
    guardWorkingHoursForTeam: "Guard Working Hours For Team",
    
    // Shift Management
    deleteShift: "Delete Shift",
    deleteShiftConfirmation: "Are you sure you want to delete this shift? This action cannot be undone.",
    deleting: "Deleting...",
    assignedGuards: "assigned guards",
    noGuardsAssigned: "No guards assigned",
    location: "Location",
    guards: "Guards",
    eventDeleted: "Event deleted",
    errorDeletingEvent: "Error deleting event",
    
    // View Modes
    dayView: "Day",
    weekView: "Week",
    monthView: "Month",
    customView: "Custom",
    totalWorkView: "Total Work",
    
    // Alert Types
    allAlerts: "All Alerts",
    insufficientHours: "Insufficient Hours",
    unassignedSchedule: "Unassigned Schedule",
    nightMorningShift: "Night-Morning Shift",
    insufficientRest: "Insufficient Rest",
    visibilityStatus: "Visibility Status",
    missingTemplateShifts: "Missing Template Shifts",
    filterByAlertType: "Filter by Alert Type",
    selectAlertType: "Select alert type",
    
    // HR Analytics
    hiringAnalytics: "Hiring Analytics",
    selectRange: "Select range",
    last30Days: "Last 30 days",
    last90Days: "Last 90 days",
    last12Months: "12 months",
    totalInterviews: "Total Interviews",
    totalInterviewsCompleted: "Total Interviews (completed)",
    range: "Range",
    days: "days",
    
    // Candidate List
    searchCandidates: "Search candidates...",
    filterByMunicipality: "Filter by Municipality...",
    allRegions: "All Regions",
    centralSector: "Central Sector",
    north: "North",
    south: "South",
    west: "West",
    east: "East",
    piraeus: "Piraeus",
    region: "Region",
    manage: "Manage",
    testResults: "Results",
    reconsider: "Reconsider",
    deleteCandidate: "Delete Candidate",
    deleteCandidateConfirmation: "Are you sure you want to delete {name}? This action cannot be undone.",
    noCandidatesFound: "No candidates found",
    candidateDeletedSuccess: "Candidate and all related data deleted successfully",
    deleted: "Deleted",
    loadingCandidates: "Loading candidates...",
    application: "Application",
    
    // Provider Evaluation
    providerEvaluations: "Provider Evaluations",
    providerEvaluationsDesc: "Submit and manage provider evaluations",
    filterByClient: "Filter by Client",
    allClients: "All Clients",
    analytics: "Analytics",
    mitigations: "Mitigations",
    newEvaluation: "New Evaluation",
    history: "History",
    customerEvaluationAnalytics: "Customer Evaluation Analytics",
    fullOverviewAndAnalysis: "Full overview and analysis of service provider evaluations",
    overallRating: "Overall Rating",
    averageOfAllMetrics: "Average of all metrics",
    wouldRecommend: "Would Recommend",
    clientsWhoWouldRecommend: "Clients who would recommend",
    wouldRenew: "Would Renew",
    clientsWhoWouldRenew: "Clients who would renew",
    clientsLowercase: "clients",
    serviceQuality: "Service Quality",
    technologyAndInnovation: "Technology & Innovation",
    valueAndRelationship: "Value & Relationship",
    allEvaluations: "All Evaluations",
    whatWorksWell: "What Works Well",
    npsScoreBreakdown: "Net Promoter Score Breakdown",
    responseTime: "Response Time",
    incidentHandling: "Incident Handling",
    guardQuality: "Guard Quality",
    professionalism: "Professionalism",
    communication: "Communication",
    technologySatisfaction: "Technology Satisfaction",
    reportingQuality: "Reporting Quality",
    innovation: "Innovation",
    valueForMoney: "Value for Money",
    contractFlexibility: "Contract Flexibility",
    accountManagement: "Account Management",
    moduleNotAvailable: "Module Not Available",
    
    // Performance Management
    selectEmployee: "Select Employee",
    selectEmployeeToViewEvaluations: "Select an employee to view evaluations",
    searchEmployee: "Search employee...",
    noEmployeesFound: "No employees found",
    refreshData: "Refresh Data",
    totalEmployees: "Total Employees",
    activeEmployees: "Active employees",
    newSystem: "New system",
    average: "Average",
    averageScoreNewSystem: "Average score (new system)",
    topPerformers: "Top Performers",
    topPerformer: "Top Performer",
    withAGrade: "with A+ or A",
    hasARating: "Has a rating",
    performanceManagement: "Performance Management",
    newBaseScoreSystem: "New Base Score System - Advanced view and evaluation management",
    shiftaskEvaluation: "Shiftask Evaluation",
    viewHistory: "View History",
    clientEvaluation: "Client Evaluation",
    newBehavior: "New Behavior",
    saving: "Saving...",
    submitEvaluation: "Submit Evaluation",
    updateEvaluation: "Update Evaluation",
    excellent: "Excellent",
    good: "Good",
    needsImprovement: "Needs Improvement",
    yesRepresentsWell: "Yes, represents {appName} well",
    noNeedsImprovement: "No, needs improvement",
    positiveCommentsFor: "Positive Comments for {appName}",
    whatImpressedYou: "What impressed you about the {appName} guard?",
    positiveCommentsPlaceholder: "e.g. Impeccable appearance, polite behavior, high level of security...",
    improvementSuggestions: "Improvement Suggestions",
    whereCouldImprove: "Where could {appName} improve its services?",
    improvementSuggestionsPlaceholder: "e.g. More training in protocols, better communication...",
    generalCommentsFor: "General Comments for {appName}",
    howDoYouRate: "How do you rate the overall cooperation with {appName}?",
    generalCommentsPlaceholder: "Your general impression of the guard and {appName}...",
    evaluationSubmittedSuccess: "Evaluation submitted successfully!",
    errorSubmittingEvaluation: "Error submitting evaluation",
    evaluationUpdatedSuccess: "Evaluation updated successfully!",
    errorUpdatingEvaluation: "Error updating evaluation",
    
// Client Evaluation Trait Descriptions
professionalismDesc: "Professional behavior and appearance of {appName} personnel",
reliability: "Reliability",
reliabilityDesc: "Consistency and responsibility in security duties",
communicationDesc: "Communication skills with your staff",
qualityOfService: "Quality of Services",
qualityOfServiceDesc: "Level of services provided",
punctualityDesc: "Timely arrival and schedule adherence",
companyRepresentation: "{appName} Representation",
companyRepresentationDesc: "How the personnel represents {appName} at your facilities",
overallSatisfaction: "Overall Satisfaction",
overallSatisfactionDesc: "General satisfaction with the {appName} personnel",
overallScore: "Overall Score",
editEvaluation: "Edit Evaluation",
evaluatePersonnel: "Evaluate {appName} Personnel",
personnelFrom: "{appName} Personnel",
active: "Active",
evaluatedBy: "Evaluated by",
evaluatePerformanceOf: "Evaluate the performance of personnel {guardName} from {appName}",
evaluationHelps: "Your evaluation helps {appName} maintain high service standards at your facilities",
pleaseSelectRating: "Please select a rating",
recommendPersonnel: "Recommendation for {appName} Personnel",
wouldRecommendPersonnel: "Would you recommend this {appName} personnel to other companies?",
operationalActivity: "Operational Activity",
supervisorReports: "Supervisor Reports",
behaviors: "Behaviors",
punctualityAndTimely: "Punctuality & Timely Check-in",
shiftsTotal: "shifts total",
supervisorReportsCount: "supervisor reports",
clientEvaluationsCount: "client evaluations",
positive: "positive",
negative: "negative",
performance: "performance",
positiveBehaviors: "Positive Behaviors",
negativeBehaviors: "Negative Behaviors",
noPositiveBehaviors: "No positive behaviors",
noNegativeBehaviors: "No negative behaviors",
loadingPerformanceData: "Loading performance data...",
couldNotCalculatePerformance: "Could not calculate performance. Please try again.",
hundredPointSystem: "100-Point Performance Evaluation System",
understandingNewSystem: "Understanding the new ideal grading system",
baseScoreFromOVIT: "Base Score from OVIT",
ofOVITPercentage: "of OVIT percentage:",
points: "points",
baseScoreCalculated: "Base score is calculated as 80% of the average score of OVIT evaluations.",
totalScore: "Total Score",
baseScore: "Base Score",
additionalPoints: "Additional Points",
shiftaskBase: "Shiftask Base",
base: "Base",
withoutEvaluation: "Without Evaluation",
operationalActivityMax10: "Operational Activity (max 10 points)",
emergencyReportsLabel: "Emergency reports:",
patrols: "Patrols:",
pointsPerReport: "points/report",
pointsPerPatrol: "points/patrol",
supervisorReportsMax: "Supervisor Reports (max ±10 points)",
basedOnPositivityNegativity: "Based on positivity or negativity of reports from supervisors.",
otherCategories: "Other Categories",
clientEvaluations: "Client evaluations",
upTo5Points: "up to ±5 points",
upTo15Points: "up to ±15 points",
punctuality: "Punctuality:",
gradeScale: "Grade Scale",
pointsCalculation: "Points Calculation",
bonusPointsPositiveOnly: "Bonus Points (Positive Only)",
negativePoints: "Negative Points",
pointsPerPatrolActivity: "points per patrol",
pointsPerEmergencyReport: "points per emergency report",
pointsForTimelyArrival: "points for timely arrival",
pointForPresenceWithin15min: "point for presence within 15min",
pointsForPositiveSupervisorReports: "points for positive supervisor reports",
pointsFromBehaviorTracking: "Points from behavior tracking",
pointsForAbsence: "points for absence",
pointsForDelay15to30min: "points for delay 15-30min",
pointsForDelayOver30min: "points for delay >30min",
pointsForNegativeReports: "points for negative reports",
negativePointsFromBehaviorRecords: "Negative points from behavior records",
negativePointsFromClientEvaluations: "Negative points from client evaluations",
evaluationCriteriaNewSystem: "Evaluation Criteria - New System",
basePointSystem: "Base Point System",
baseScoreFromOVITEvaluation: "Base score comes from OVIT evaluation (0-80 points). Without OVIT evaluation, base score is 0 points. Additional points are added/subtracted based on:",
positivePoints: "Positive Points:",
to: "to",
more: "more",
from: "from",
hasShiftaskEvaluation: "Has Shiftask Evaluation",
activeEvaluation: "Active Evaluation",
currentEvaluationForPerformance: "Current evaluation used for performance calculation",
    editEvaluation: "Edit Evaluation",
    evaluatePersonnel: "Evaluate {appName} Personnel",
    personnelFrom: "{appName} Personnel",
    active: "Active",
    evaluatedBy: "Evaluated by",
    evaluatePerformanceOf: "Evaluate the performance of personnel {guardName} from {appName}",
    evaluationHelps: "Your evaluation helps {appName} maintain high service standards at your facilities",
    pleaseSelectRating: "Please select a rating",
    recommendPersonnel: "Recommendation for {appName} Personnel",
    wouldRecommendPersonnel: "Would you recommend this {appName} personnel to other companies?",
    
    // Shiftask Evaluation Modal
    newShiftaskEvaluation: "New Shiftask Evaluation",
    internalEvaluation: "Internal Evaluation",
    firstSemester: "1st Semester",
    secondSemester: "2nd Semester",
    evaluationProgram: "Evaluation progress",
    rateOnScale: "Rate on scale 1-7",
    veryLow: "Very Low",
    low: "Low",
    belowAverage: "Below Average",
    aboveAverage: "Above Average",
    high: "High",
    commentsOptional: "Comments (optional)",
    addExamplesPlaceholder: "Add specific examples or explanations...",
    previous: "Previous",
    next: "Next",
    complete: "Complete",
    personalQualities: "Personal Qualities",
    professionalSkills: "Professional Skills",
    teamCollaboration: "Team Collaboration",
    loadingEvaluationCriteria: "Loading evaluation criteria...",
    overallEvaluationScore: "Overall Evaluation Score",
    basedOnCriteria: "Based on {count} evaluation criteria",
    analysisByCategory: "Analysis by Category",
    criteria: "criteria",
    generalNotes: "General Notes",
    addGeneralNotesPlaceholder: "Add general notes for the evaluation...",
    backToQuestions: "Back to Questions",
    cancel: "Cancel",
    saveEvaluation: "Save Evaluation",
    shiftaskEvaluationSavedSuccess: "Shiftask evaluation saved successfully!",
    errorSavingEvaluation: "Error saving evaluation",
    
    // Evaluation Criteria Translations
    ethicsAndIntegrity: "Ethics and Integrity",
    ethicsDesc: "Ethical behavior and character integrity",
    adaptability: "Adaptability",
    adaptabilityDesc: "Ability to adapt to new situations",
    responsibility: "Responsibility",
    responsibilityDesc: "Sense of responsibility and accountability",
    dedication: "Dedication",
    dedicationDesc: "Dedication and devotion to duty",
    crisisResponse: "Crisis Response",
    crisisResponseDesc: "Ability to manage crises and emergencies",
    discretion: "Discretion",
    discretionDesc: "Discretion and confidentiality",
    alertness: "Alertness",
    alertnessDesc: "Attention and vigilance during service",
    appearance: "Appearance",
    appearanceDesc: "Professional appearance and presentation",
    professionalismEvalDesc: "Professional conduct and attitude",
    technicalSkills: "Technical Skills",
    technicalSkillsDesc: "Technical knowledge and skills",
    physicalCondition: "Physical Condition",
    physicalConditionDesc: "Physical preparation and endurance",
    communicationEvalDesc: "Effective and clear communication",
    leadershipSkills: "Leadership Skills",
    leadershipSkillsDesc: "Ability to lead and take initiative",
    teamwork: "Teamwork",
    teamworkDesc: "Ability to cooperate and work in a team",
    respect: "Respect",
    respectDesc: "Respect for colleagues and superiors",
    consistency: "Consistency",
    consistencyDesc: "Consistency and punctuality",
    cooperation: "Cooperation",
    cooperationDesc: "Cooperation with colleagues and supervisors",
    reliabilityEvalDesc: "Reliability and trustworthiness",
    technicalKnowledge: "Technical Knowledge",
    technicalKnowledgeDesc: "Knowledge of equipment and security procedures",
    initiative: "Initiative",
    initiativeDesc: "Ability to take initiative and act proactively",
    organization: "Organization",
    organizationDesc: "Work organization and time management ability",
    details: "Details",
    noDataAvailableForMetric: "No data available for this metric.",
    clientEvaluations: "Client Evaluations",
    recordBehavior: "Record Behavior",
    recordBehaviorFor: "Record behavior for",
    recordEmployeeBehavior: "Record employee behavior",
    behaviorCategory: "Behavior Category",
    behaviorType: "Behavior Type",
    incidentDate: "Incident Date",
    incidentDescription: "Incident Description",
    describeIncidentPlaceholder: "Describe the incident...",
    evidenceProof: "Evidence/Proof",
    describeEvidencePlaceholder: "Describe any evidence or proof...",
    recording: "Recording...",
    loadingBehaviorTypes: "Loading behavior types...",
    noBehaviorTypesFound: "No behavior types found",
    points: "points",
    selectDate: "Select date",
    gradingScale: "Grading Scale",
    pointsCalculation: "Points Calculation",
    baseScoreFrom: "Base Score from {appName}",
    ofPercentage: "of {appName} percentage:",
    baseScoreCalculated: "Base score is calculated as 80% of the average score of {appName} evaluations.",
    emergency: "Emergency",
    clients: "Clients",
    shifts: "Shifts",
    operationalActivity: "Operational Activity",
    supervisorReports: "Supervisor Reports",
    behaviors: "Behaviors",
    punctualityAndTimely: "Punctuality & Timely Check-in",
    shiftsTotal: "shifts total",
    supervisorReportsCount: "supervisor reports",
    clientEvaluationsCount: "client evaluations",
    positive: "positive",
    negative: "negative",
    performance: "performance",
    positiveBehaviors: "Positive Behaviors",
    negativeBehaviors: "Negative Behaviors",
    noPositiveBehaviors: "No positive behaviors",
    noNegativeBehaviors: "No negative behaviors",
    loadingPerformanceData: "Loading performance data...",
    couldNotCalculatePerformance: "Could not calculate performance. Please try again.",
    hundredPointSystem: "100-Point Performance Evaluation System",
    understandingNewSystem: "Understanding the new ideal grading system",
    baseScoreFromOVIT: "Base Score from OVIT",
    ofOVITPercentage: "of OVIT percentage:",
    points: "points",
    baseScoreCalculated: "Base score is calculated as 80% of the average score of OVIT evaluations.",
    totalScore: "Total Score",
    baseScore: "Base Score",
    additionalPoints: "Additional Points",
    shiftaskBase: "Shiftask Base",
    base: "Base",
    withoutEvaluation: "Without Evaluation",
    operationalActivityMax10: "Operational Activity (max 10 points)",
    emergencyReportsLabel: "Emergency reports:",
    patrols: "Patrols:",
    pointsPerReport: "points/report",
    pointsPerPatrol: "points/patrol",
    supervisorReportsMax: "Supervisor Reports (max ±10 points)",
    basedOnPositivityNegativity: "Based on positivity or negativity of reports from supervisors.",
    otherCategories: "Other Categories",
    clientEvaluations: "Client evaluations",
    upTo5Points: "up to ±5 points",
    upTo15Points: "up to ±15 points",
    punctuality: "Punctuality:",
    gradeScale: "Grade Scale",
    pointsCalculation: "Points Calculation",
    bonusPointsPositiveOnly: "Bonus Points (Positive Only)",
    negativePoints: "Negative Points",
    pointsPerPatrolActivity: "points per patrol",
    pointsPerEmergencyReport: "points per emergency report",
    pointsForTimelyArrival: "points for timely arrival",
    pointForPresenceWithin15min: "point for presence within 15min",
    pointsForPositiveSupervisorReports: "points for positive supervisor reports",
    pointsFromBehaviorTracking: "Points from behavior tracking",
    pointsForAbsence: "points for absence",
    pointsForDelay15to30min: "points for delay 15-30min",
    pointsForDelayOver30min: "points for delay >30min",
    pointsForNegativeReports: "points for negative reports",
    negativePointsFromBehaviorRecords: "Negative points from behavior records",
    negativePointsFromClientEvaluations: "Negative points from client evaluations",
    evaluationCriteriaNewSystem: "Evaluation Criteria - New System",
    basePointSystem: "Base Point System",
    baseScoreFromOVITEvaluation: "Base score comes from OVIT evaluation (0-80 points). Without OVIT evaluation, base score is 0 points. Additional points are added/subtracted based on:",
    positivePoints: "Positive Points:",
    to: "to",
    more: "more",
    from: "from",
    hasShiftaskEvaluation: "Has Shiftask Evaluation",
    activeEvaluation: "Active Evaluation",
    currentEvaluationForPerformance: "Current evaluation used for performance calculation",
    
    // Behavior Names
    inappropriateBehavior: "Inappropriate Behavior",
    postAbandonment: "Post Abandonment",
    delay: "Delay",
    absence: "Absence",
    positiveReport: "Positive Report",
    negativeReport: "Negative Report",
    timelyArrival: "Timely Arrival",
    presenceWithin15min: "Presence Within 15min",
    consistency: "Consistency",
    initiative: "Initiative",
    cooperation: "Cooperation",
    
    distinctionBadge: "Distinction Badge",
    distinctionBadgeDescription: "Employees who achieve a score above 100 earn the distinction badge and appear as '100+ (A+)' on their profile, indicating exceptional performance.",
    bonusPointsPositiveOnlyShort: "Bonus Points (Positive Only)",
    performancePoints: "Performance Points (±)",
    patrolsPointsRange: "Patrols: 1-5 points depending on number",
    emergencyReportsPointsRange: "Emergency reports: 1 point per report (up to 3)",
    punctualityBonusForPerfect: "Punctuality: Bonus for perfect attendance",
    supervisorReportsPositivityNegativity: "Supervisor reports: Based on positivity/negativity",
    clientEvaluationsBasedOnRating: "Client evaluations: Based on rating 1-5",
    behaviorsPositiveNegative: "Behaviors: Positive/negative behaviors",
    aPlusAndAGrades: "A+ and A ratings",
    newBaseScoreSystemDetailed: "New Base Score System - Detailed performance view",
    selectEmployeeFromList: "Select an employee from the list to view their evaluations",
    successGuide: "Success Guide",
    
    // Equipment Management
    suppliers: "Suppliers",
    supplierManagement: "Supplier Management",
    addSupplier: "Add Supplier",
    editSupplier: "Edit Supplier",
    supplierName: "Supplier Name",
    registrationNumber: "Registration Number",
    website: "Website",
    evaluationDate: "Evaluation Date",
    qualityScore: "Quality Score",
    deliveryPerformance: "Delivery Performance",
    pricingCompetitiveness: "Pricing Competitiveness",
    complianceCertification: "Compliance & Certification",
    communicationResponsiveness: "Communication & Responsiveness",
    technicalSupport: "Technical Support",
    overallScore: "Overall Score",
    improvementActions: "Improvement Actions",
    companyAssets: "Company Assets",
    assetManagement: "Asset Management",
    addAsset: "Add Asset",
    editAsset: "Edit Asset",
    assetName: "Asset Name",
    assetCategory: "Asset Category",
    purchasePrice: "Purchase Price",
    currentValue: "Current Value",
    depreciationRate: "Depreciation Rate (%)",
    maintenanceSchedule: "Maintenance Schedule",
    lastMaintenanceDate: "Last Maintenance Date",
    nextMaintenanceDate: "Next Maintenance Date",
    
    // Common Actions
    view: "View",
    edit: "Edit",
    delete: "Delete",
    add: "Add",
    create: "Create",
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    filter: "Filter",
    search: "Search",
    back: "Back",
    backTo: "Back to",
    next: "Next",
    previous: "Previous",
    close: "Close",
    open: "Open",
    refresh: "Refresh",
    download: "Download",
    upload: "Upload",
    export: "Export",
    import: "Import",
    print: "Print",
    share: "Share",
    copy: "Copy",
    paste: "Paste",
    cut: "Cut",
    undo: "Undo",
    redo: "Redo",
    
    // Common Status
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    
    // Common Messages
    pleaseWait: "Please wait...",
    warning: "Warning",
    info: "Info",
    
    // Common Forms
    required: "Required",
    optional: "Optional",
    selectOption: "Select an option",
    enterValue: "Enter a value",
    chooseFile: "Choose file",
    dragDropFile: "Drag and drop file here",
    or: "or",
    and: "and",
    
    // Common Filters
    filters: "Filters",
    searchPlaceholder: "Search...",
    clearFilters: "Clear filters",
    applyFilters: "Apply filters",
    noResults: "No results found",
    showMore: "Show more",
    showLess: "Show less",
    
    // Common Tables
    description: "Description",
    details: "Details",
    type: "Type",
    category: "Category",
    tags: "Tags",
    
    // Common Pagination
    page: "Page",
    pages: "Pages",
    first: "First",
    last: "Last",
    goToPage: "Go to page",
    rowsPerPage: "Rows per page",
    totalItems: "Total items",
    
    // Common Time
    today: "Today",
    yesterday: "Yesterday",
    tomorrow: "Tomorrow",
    thisWeek: "This week",
    lastWeek: "Last week",
    thisMonth: "This month",
    lastMonth: "Last month",
    thisYear: "This year",
    lastYear: "Last year",
    custom: "Custom",
    selectDate: "Select date",
    selectTime: "Select time",
    selectDateTime: "Select date and time",
    
    // Common Confirmations
    areYouSure: "Are you sure?",
    confirmDelete: "Confirm delete",
    confirmCancel: "Confirm cancel",
    thisActionCannotBeUndone: "This action cannot be undone",
    yes: "Yes",
    no: "No",
    ok: "OK",
    
    // Common Errors
    somethingWentWrong: "Something went wrong",
    pleaseTryAgain: "Please try again",
    errorOccurred: "An error occurred",
    invalidInput: "Invalid input",
    requiredField: "This field is required",
    invalidEmail: "Invalid email address",
    invalidPhone: "Invalid phone number",
    invalidPassword: "Invalid password",
    passwordsDoNotMatch: "Passwords do not match",
    
    // Common Success
    savedSuccessfully: "Saved successfully",
    deletedSuccessfully: "Deleted successfully",
    updatedSuccessfully: "Updated successfully",
    createdSuccessfully: "Created successfully",
    operationSuccessful: "Operation successful",
    loadingData: "Loading data...",
    
    // Common Labels
    hours: "Hours",
    total: "Total",
    notes: "Notes",
    comments: "Comments",
    attachments: "Attachments",
    
    // Time Greetings
    goodMorning: "Good Morning",
    goodAfternoon: "Good Afternoon",
    goodEvening: "Good Evening",
    
    // Dashboard Items
    enhancedDashboard: "Enhanced Dashboard",
    enhancedDashboardDesc: "Advanced security and system management",
    hrManagementDesc: "Manage candidates, interviews, and HR processes",
    leaveRequestsDesc: "Submit and manage leave requests",
    facilityPresenceDesc: "Monitor facility presence and occupancy",
    applicationDocumentation: "Application Documentation",
    applicationDocumentationDesc: "View complete system documentation",
    patrolMonitoringSystem: "Patrol monitoring system",
    manageAdministrativeTeam: "Manage administrative team",
    
    // Error Messages
    
    // Success Messages
    
    // Form Validation
    
    // Performance Management (continued)
    successGuideTitle: "Evaluation Success Guide",
    successGuideDescription: "The complete guide to achieving excellent evaluation in the 100-point system. Learn where to focus and how to improve your performance.",
    priorities: "Priorities",
    timeline: "Timeline",
    distinction: "Distinction",
    tips: "Tips",
    highPriority: "High Priority",
    mediumPriority: "Medium Priority",
    lowPriority: "Low Priority",
    ovitEvaluations: "OVIT Evaluations",
    mostImportantPart: "The most important part of your evaluation",
    prepareWellForOVIT: "Prepare well for each OVIT evaluation",
    studyCriteriaBeforehand: "Study evaluation criteria beforehand",
    askFeedbackFromSupervisors: "Ask for feedback from your supervisors",
    applyRecommendations: "Apply recommendations from previous evaluations",
    behaviorsCategory: "Behaviors",
    largeImpactOnFinalScore: "Large impact on final score",
    positiveAndNegativeBehaviors: "Positive and negative behaviors",
    avoidNegativeBehaviors: "Avoid negative behaviors (delays, omissions)",
    cultivatePositiveBehaviors: "Cultivate positive behaviors (proactivity, cooperation)",
    participateActivelyInTeam: "Participate actively in team activities",
    showInitiativeAndResponsibility: "Show initiative and responsibility",
    operationalActivityCategory: "Operational Activity",
    supplementaryPoints: "Supplementary points",
    dailyActivitiesAndTasks: "Daily activities and tasks",
    completeAllDailyTasks: "Complete all daily tasks",
    recordYourActivities: "Record your activities",
    participateInAdditionalTasks: "Participate in additional tasks when requested",
    maintainGoodWorkRecords: "Maintain good work records",
    supervisorReportsCategory: "Supervisor Reports",
    feedbackFromManagers: "Feedback from managers",
    evaluationFromSupervisors: "Evaluation from your supervisors",
    maintainGoodCommunication: "Maintain good communication with supervisors",
    askForRegularFeedback: "Ask for regular feedback",
    applyReceivedRecommendations: "Apply the recommendations you receive",
    showYouLearnFromMistakes: "Show that you learn from your mistakes",
    clientEvaluationsCategory: "Client Evaluations",
    clientSatisfaction: "Client satisfaction",
    feedbackFromClients: "Feedback from clients",
    maintainProfessionalBehavior: "Maintain professional behavior",
    respondQuicklyToClientNeeds: "Respond quickly to client needs",
    communicateEffectively: "Communicate effectively",
    solveProblemsProactively: "Solve problems proactively",
    week12: "Week 1-2",
    foundation: "Foundation",
    reviewOVITCriteria: "Review OVIT evaluation criteria",
    setPersonalPerformanceGoals: "Set personal performance goals",
    discussExpectationsWithSupervisor: "Discuss expectations with supervisor",
    week34: "Week 3-4",
    implementation: "Implementation",
    applyBestPractices: "Apply best practices",
    recordPositiveActivities: "Record positive activities",
    avoidNegativeBehaviorsAction: "Avoid negative behaviors",
    month23: "Month 2-3",
    optimization: "Optimization",
    evaluateProgress: "Evaluate progress",
    adjustStrategy: "Adjust strategy",
    seekAdditionalImprovementOpportunities: "Seek additional improvement opportunities",
    month4Plus: "Month 4+",
    maintainExcellence: "Maintain Excellence",
    continuousImprovement: "Continuous improvement",
    mentorNewColleagues: "Mentor new colleagues",
    targetDistinctionBadge: "Target Distinction Badge (100+)",
    positiveBehaviorsTitle: "Positive Behaviors",
    negativeBehaviorsTitle: "Negative Behaviors",
    timelyArrivalAtWork: "Timely arrival at work",
    initiativeInEmergencies: "Initiative in emergencies",
    helpingColleagues: "Helping colleagues",
    proactiveReportingOfProblems: "Proactive reporting of problems",
    participationInTraining: "Participation in training",
    delayAtWork: "Delay at work",
    omissionOfDuties: "Omission of duties",
    nonCooperativeAttitude: "Non-cooperative attitude",
    violationOfRegulations: "Violation of regulations",
    clientComplaints: "Client complaints",
    monthlyActionPlan: "Monthly Action Plan",
    strategyForDistinctionBadge: "Strategy for Distinction Badge (100+ points)",
    step1SecureSolidFoundation: "Step 1: Secure Solid Foundation",
    targetBaseScore75Plus: "75+ points in base score",
    achieveExcellentOVITPerformance: "Achieve excellent performance in OVIT evaluations",
    maintainConsistentDailyPerformance: "Maintain consistent daily performance",
    avoidAllNegativeBehaviors: "Avoid all negative behaviors",
    step2CollectBonusPoints: "Step 2: Collect Bonus Points",
    target1525ExtraPoints: "15-25 additional points",
    accumulatePositiveBehaviors: "Accumulate positive behaviors",
    secureExcellentClientEvaluations: "Secure excellent client evaluations",
    participateInAllOperationalActivities: "Participate in all operational activities",
    step3TargetExcellence: "Step 3: Target Excellence",
    target100PlusTotal: "100+ points total",
    becomeRoleModel: "Become a role model for others",
    takeLeadershipRole: "Take a leadership role",
    contributeToProcessImprovement: "Contribute to process improvement",
    pointsDistributionForDistinction: "Points Distribution for Distinction",
    ovitEvaluationTarget75Plus: "OVIT Evaluation (Target: 75+)",
    positiveBehaviorsTarget10Plus: "Positive Behaviors (Target: 10+)",
    operationalActivityTarget8Plus: "Operational Activity (Target: 8+)",
    clientEvaluationsTarget4Plus: "Client Evaluations (Target: 4+)",
    totalTargetScore: "Total Target Score",
    generalTips: "General Tips",
    monitorYourProgressRegularly: "Monitor your progress regularly",
    askFeedbackFromSupervisorsAndColleagues: "Ask for feedback from supervisors and colleagues",
    focusOnHighestImpactAreas: "Focus on areas with the highest impact",
    maintainConsistentPerformance: "Maintain consistent performance throughout the period",
    improvementResources: "Improvement Resources",
    participateInTrainingPrograms: "Participate in training programs",
    seekMentorOrExperiencedColleague: "Seek a mentor or experienced colleague",
    studyIndustryBestPractices: "Study industry best practices",
    manageYourTimeEffectively: "Manage your time effectively",
    remember: "Remember",
    successIsNotJustAboutPoints: "Success in evaluation is not just about points - it's about continuous improvement and growth as a professional. Every step you take brings you closer to excellence.",
    excellent: "Excellent",
    fair: "Fair",
    poor: "Poor",
    itEquipment: "IT Equipment",
    itEquipmentManagement: "IT Equipment Management",
    equipmentName: "Equipment Name",
    equipmentType: "Equipment Type",
    licenseKey: "License Key",
    ipAddress: "IP Address",
    macAddress: "MAC Address",
    specifications: "Specifications",
    maintenanceNotes: "Maintenance Notes",
    server: "Server",
    workstation: "Workstation",
    laptop: "Laptop",
    network: "Network Equipment",
    software: "Software",
    guardEquipmentList: "Guard Equipment List",
    teamEquipmentList: "Team Equipment List",
    guardsEquipment: "Equipment Assigned to Guards",
    teamsEquipment: "Equipment Assigned to Teams",
    totalEquipmentValue: "Total Equipment Value",
    unitPrice: "Unit Price",
    totalPrice: "Total Price",
    supplier: "Supplier",
    selectSupplier: "Select Supplier",
    companyPolicies: "Company Policies",
    codeOfEthics: "Code of Ethics",
    codeOfConduct: "Code of Conduct",
    gdprPolicy: "GDPR Policy",
    uploadPolicy: "Upload Policy",
    policyAcknowledgement: "Policy Acknowledgement",
    acknowledgePolicies: "Acknowledge Policies",
    iHaveReadAndAgree: "I have read and agree to comply with the",
    acceptAndAcknowledge: "Accept & Acknowledge",
    mustAcknowledgePolicies: "You must acknowledge all policies to continue",
    policyUploadedSuccessfully: "Policy uploaded successfully",
    acknowledgedUsers: "Acknowledged Users",
    acknowledged: "Acknowledged",
    pendingUsers: "Pending Users",
    acknowledgementRate: "Acknowledgement Rate",
    viewPolicyStats: "View Policy Statistics",
    policyStatistics: "Policy Acknowledgement Statistics",
    noActivePolicy: "No active policy available",
    uploadNewVersion: "Upload New Version",
    customTabName: "Custom Tab Name",
    customTabNameHelp: "Customize the name of the Ethics & Compliance tab in the dashboard",
    uploadNewVersionNote: "Uploading a new version will require users to re-acknowledge",
    moduleDisabledMessage: "This module is currently disabled for your organization. Please contact your administrator to enable it.",
    moduleManagement: "Module Management",
    moduleManagementDesc: "Enable or disable system modules",
    enableDisableModules: "Enable or disable modules for your organization",
    moduleManagementInfo: "Control which features are available to your organization. Disabling a module will hide it from all users.",
    ethicsComplianceModuleDesc: "Company policies, code of ethics, and compliance management",
    equipmentManagementModuleDesc: "Equipment types, assignments, and inventory tracking",
    supplierEvaluationsModuleDesc: "Supplier management and ISO 9001 evaluations",
    currentVersion: "Current Version",
    policyType: "Policy Type",
    viewPolicy: "View Policy",
    downloadPolicy: "Download Policy",
    policiesRequireAcknowledgement: "Company Policies Require Your Acknowledgement",
    pleaseReviewPolicies: "Please review and acknowledge the following company policies before continuing. These policies are essential for maintaining ethical standards and compliance.",
    iPledgeToComply: "I pledge to read, understand, and comply with all company policies",
    noSupplier: "No Supplier",
    quarter1: "Q1",
    quarter2: "Q2",
    quarter3: "Q3",
    quarter4: "Q4",
    annual: "Annual",
    vehicle: "Vehicle",
    building: "Building",
    furniture: "Furniture",
    criteria: "Criteria",
    selectUser: "Select User",
    viewEvaluation: "View Evaluation",
    securityInventory: "Security Inventory",
    recordSupplierStrengths: "Record the supplier's strengths...",
    recordSupplierWeaknesses: "Record the supplier's weaknesses...",
    suggestImprovements: "Suggest improvement actions for the collaboration...",
    supplierHasIsoCertification: "The supplier has ISO certification",
    evaluationStatus: "Evaluation Status",
    nextEvaluationDate: "Next Evaluation",
    
    
    
  },
    fr: {
        settingsTitle: "Paramètres",
        personalizeWorkspace: "Personnalisez votre espace de travail et gérez vos préférences avec notre interface belle et intuitive",
        navigation: "Navigation",
        general: "Général",
        generalDescription: "Compte de base et préférences",
        manageYourProfile: "Gérer votre profil",
        customizeInterface: "Personnaliser votre interface",
        controlYourAlerts: "Contrôler vos alertes",
        dataManagement: "Gestion des données",
        dataManagementDescription: "Rapports, sauvegardes et intégrations",
        configureReportSettings: "Configurer les paramètres de rapport",
        manageDataBackups: "Gérer les sauvegardes de données",
        externalConnections: "Connexions externes",
        equipmentDescription: "Gérer les types d'équipement et les affectations",
        guardEquipmentTypes: "Types d'équipement de garde",
        guardEquipmentCatalog: "Catalogue d'équipement de garde",
        teamEquipmentTypes: "Types d'équipement d'équipe",
        teamEquipmentCatalog: "Catalogue d'équipement d'équipe",
        assignEquipmentToGuards: "Assigner l'équipement aux gardes",
        guardAssignments: "Affectations de gardes",
        assignEquipmentToTeams: "Assigner l'équipement aux équipes",
        teamAssignments: "Affectations d'équipes",
        financeDescription: "Paramètres financiers et gestion des coûts",
        configureHourlyRates: "Configurer les tarifs horaires",
        schedulingDescription: "Gestion des quarts et fonctionnalités de planification",
        enableShiftSwapRequests: "Activer les demandes d'échange de quart",
        systemAdministration: "Administration du système",
        systemAdministrationDescription: "Paramètres système avancés et gestion des utilisateurs",
        manageUsersAndRoles: "Gérer les utilisateurs et les rôles",
        adminPrivilegesMessage: "Vous avez des privilèges administratifs et pouvez accéder aux options de configuration avancées.",
        
        // Account Settings
        account: "Compte",
        appearance: "Apparence",
        notifications: "Notifications",
        reports: "Rapports",
        backup: "Sauvegarde",
        integrations: "Intégrations",
        equipment: "Équipement",
        finance: "Finance",
        hourlyCosts: "Coûts Horaires",
        shiftSwaps: "Échanges de Quart",
        userManagement: "Gestion des Utilisateurs",
        adminAccess: "Accès Administrateur",
        accountLimits: "Limites du Compte",
        yourCurrentAccountUsageLimits: "Vos limites d'utilisation actuelles du compte définies par l'administrateur",
        moduleAccess: "Accès aux Modules",
        availableAndDisabledModules: "Modules disponibles et désactivés pour votre compte (informatif uniquement)",
        profileInformation: "Informations du Profil",
        updateYourPersonalInformation: "Mettez à jour vos informations personnelles et vos coordonnées",
        profilePicture: "Photo de Profil",
        changePhoto: "Changer la Photo",
        removePhoto: "Supprimer la Photo",
        recommendedSquareImage: "Recommandé : Image carrée, au moins 400x400px",
        basicInformation: "Informations de Base",
        emailCannotBeChanged: "L'email ne peut pas être modifié",
        fullName: "Nom Complet",
        enterYourFullName: "Entrez votre nom complet",
        contactInformation: "Informations de Contact",
        phone: "Téléphone",
        enterPhoneNumber: "Entrez le numéro de téléphone",
        enterMobilePhoneNumber: "Entrez le numéro de téléphone portable",
        enterYourAddress: "Entrez votre adresse",
        emergencyContact: "Contact d'Urgence",
        emergencyContactNumber: "Numéro de contact d'urgence",
        additionalInformation: "Informations Supplémentaires",
        afmTaxId: "AFM (Numéro d'Identification Fiscale)",
        enterAFM: "Entrez l'AFM",
        identityNumber: "Numéro d'Identité",
        enterIdentityNumber: "Entrez le numéro d'identité",
        saveChanges: "Enregistrer les Modifications",
        profileUpdatedSuccessfully: "Profil mis à jour avec succès",
        avatarUpdatedSuccessfully: "Avatar mis à jour avec succès",
        avatarRemovedSuccessfully: "Avatar supprimé avec succès",
        uploadFailed: "Échec du téléchargement",
        removeFailed: "Échec de la suppression",
        couldNotUploadAvatar: "Impossible de télécharger l'avatar",
        couldNotRemoveAvatar: "Impossible de supprimer l'avatar",
        errorLoadingProfile: "Erreur lors du chargement du profil",
        errorUpdatingProfile: "Erreur lors de la mise à jour du profil",
        userNotFound: "Utilisateur non trouvé",
        failedToLoadProfile: "Échec du chargement du profil",
        failedToUpdateProfile: "Échec de la mise à jour du profil",
        uploading: "Téléchargement...",
        firstName: "Prénom",
        lastName: "Nom",
        enterFirstName: "Entrez votre prénom",
        enterLastName: "Entrez votre nom",
        mobilePhone: "Téléphone Portable",
        address: "Adresse",
        dateOfBirth: "Date de Naissance",
        totalUsers: "Utilisateurs Totaux",
        teams: "Équipes",
        managers: "Gestionnaires",
    
    // Appearance Settings
    brandingAppearanceCustomization: "Personnalisation de la Marque & de l'Apparence",
    contributorAppearanceMessage: "En tant que contributeur, vos paramètres d'apparence sont gérés par votre compte administrateur parent. Cela garantit une image de marque cohérente dans toute votre organisation!",
    inheritThemeMessage: "Vous hériterez automatiquement du thème, des couleurs et de la marque de votre administrateur.",
    
    // Notification Preferences
    manageNotificationPreferences: "Gérez la façon dont vous recevez les notifications",
    notificationChannels: "Canaux de Notification",
    notificationTypes: "Types de Notification",
    emailNotifications: "Notifications Email",
    chooseEmailNotifications: "Choisissez les notifications que vous souhaitez recevoir par email",
    receiveNotificationsViaEmail: "Recevoir des notifications par email",
    pushNotifications: "Notifications Push",
    receivePushNotifications: "Recevez des notifications instantanées dans votre navigateur",
    receivePushNotificationsOnDevice: "Recevoir des notifications push sur votre appareil",
    securityNotifications: "Notifications de Sécurité",
    importantSecurityNotifications: "Notifications importantes liées à la sécurité",
    shiftReminders: "Rappels de Quart",
    getNotifiedUpcomingShifts: "Recevez des notifications sur les quarts à venir",
    scheduleChanges: "Changements d'Horaire",
    notifiedScheduleModified: "Être notifié lorsque votre horaire est modifié",
    notificationsAboutScheduleUpdates: "Notifications sur les mises à jour d'horaire",
    emergencyAlerts: "Alertes d'Urgence",
    criticalAlertsImmediate: "Alertes critiques nécessitant une attention immédiate",
    loginAlerts: "Alertes de Connexion",
    notifiedLoginAttempts: "Recevez des notifications sur les nouvelles tentatives de connexion",
    passwordChanges: "Changements de Mot de Passe",
    notificationsPasswordModifications: "Notifications sur les modifications de mot de passe",
    securityNotificationsCannotDisabled: "Les notifications de sécurité ne peuvent pas être désactivées pour des raisons de sécurité. Celles-ci incluent les alertes de connexion et les notifications de changement de mot de passe.",
    mobilePushNotifications: "Notifications Push Mobile",
    receiveNotificationsMobile: "Recevoir des notifications sur votre appareil mobile",
    desktopNotifications: "Notifications Bureau",
    browserDesktopNotifications: "Notifications de bureau du navigateur",
    soundAlerts: "Alertes Sonores",
    playSoundImportant: "Jouer un son pour les notifications importantes",
    noChanges: "Aucun changement",
    preferencesUpdated: "Préférences Mises à Jour",
    notificationPreferencesSaved: "Vos préférences de notification ont été enregistrées.",
    smsAlerts: "Alertes SMS",
    receiveCriticalAlertsViaSms: "Recevoir des alertes critiques par SMS",
    incidentReports: "Rapports d'Incident",
    notificationsAboutSecurityIncidents: "Notifications sur les incidents de sécurité",
    serviceAccountsActivity: "Activité des Comptes de Service",
    notificationsAboutServiceAccountUsage: "Notifications sur l'utilisation des comptes de service",
    savePreferences: "Enregistrer les Préférences",
    
    // Module names
    facilityPresence: "Facility Presence",
    missions: "Missions",
    whistleblowers: "Whistleblowers",
    ethicsCompliance: "Ethics & Compliance",
    equipmentManagement: "Equipment Management",
    supplierEvaluations: "Supplier Evaluations",
    uploadMultipleGuards: "Téléchargez plusieurs gardes à la fois à l'aide d'une feuille de calcul Excel.",
    filterSearchPersonnel: "Filtrer et rechercher du personnel",
    teamFilter: "Équipe",
    roleFilter: "Rôle",
    noPersonnelFound: "Aucun personnel trouvé",
    getStartedAddGuard: "Commencez par ajouter votre premier garde en utilisant les boutons ci-dessus.",
    noGuardsMatchFilters: "Aucun garde ne correspond à vos filtres actuels.",
    showingGuards: "Affichage",
    guardsCount: "gardes",
    
    // HR Management Dashboard
    backToDashboard: "Retour au Tableau de Bord",
    humanResourcesManagement: "Gestion des Ressources Humaines",
    comprehensiveRecruitment: "Système complet de recrutement et de gestion des talents",
    activeRecruitment: "Recrutement Actif",
    smartAssessment: "Évaluation Intelligente",
    realTimeTracking: "Suivi en Temps Réel",
    downloadHiringProcess: "Télécharger le Processus d'Embauche",
    downloadProcess: "Télécharger le Processus",
    addCandidate: "Ajouter un Candidat",
    activeCandidates: "Candidats Actifs",
    totalInProcess: "Total en cours",
    pendingInterviews: "Entretiens en Attente",
    scheduled: "Planifiés",
    pendingTests: "Tests en Attente",
    awaitingResults: "En attente de résultats",
    readyToHire: "Prêt à Embaucher",
    completedAllStages: "Terminé toutes les étapes",
    candidates: "Candidats",
    tests: "Tests",
    workflowTab: "Flux de Travail",
    hired: "Embauchés",
    hrManagementSystem: "Système de Gestion des Ressources Humaines",
    
    // Requests
    submitNewRequest: "Soumettre une nouvelle demande",
    leaveType: "Type de congé",
    startDate: "Date de début",
    endDate: "Date de fin",
    pickDate: "Choisir une date",
    reasonOptional: "Raison (Optionnel)",
    leaveRequestInfo: "Veuillez fournir des informations supplémentaires sur votre demande de congé",
    authenticationRequired: "Authentification requise",
    mustBeLoggedIn: "Vous devez être connecté pour soumettre une demande de congé",
    success: "Succès !",
    leaveRequestSubmitted: "Votre demande de congé a été soumise",
    error: "Erreur",
    failedToSubmit: "Échec de la soumission de la demande de congé",
    selectLeaveType: "Sélectionnez un type de congé",
    repo: "REPO",
    leave: "Congé",
    other: "Autre",
    Requests: "Demandes",
    "Submit and manage requests": "Soumettre et gérer les demandes",
    "All Requests": "Toutes les demandes",
    "My Requests": "Mes demandes",
    
    // Training Management
    loadingTrainingModules: "Chargement des modules de formation...",
    trainingManagement: "Gestion de la formation",
    trainingDashboard: "Tableau de bord de formation",
    trainingModules: "Modules de formation",
    trainingProgress: "Progression de la formation",
    certifications: "Certifications",
    myTraining: "Ma formation",
    allModules: "Tous les modules",
    startTraining: "Commencer la formation",
    continueTraining: "Continuer la formation",
    reviewTraining: "Revoir la formation",
    completionRate: "Taux d'achèvement",
    overallProgress: "Progression globale",
    difficulty: "Difficulté",
    duration: "Durée",
    minutes: "minutes",
    beginner: "Débutant",
    easy: "Facile",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
    expert: "Expert",
    certificate: "Certificat",
    certified: "Certifié",
    takeQuiz: "Passer le quiz",
    retakeQuiz: "Reprendre le quiz",
    quizResults: "Résultats du quiz",
    passingScore: "Score de réussite",
    yourScore: "Votre score",
    timeSpent: "Temps passé",
    attempts: "Tentatives",
    scheduledSessions: "Sessions programmées",
    upcomingTraining: "Formation à venir",
    assignedTraining: "Formation assignée",
    optionalTraining: "Formation optionnelle",
    mandatoryTraining: "Formation obligatoire",
    
    // Guard Form Fields
    email: "Email",
    code: "Code",
    afm: "AFM",
    hiringDate: "Date d'embauche",
    licenseExpiryDate: "Date d'expiration de la licence",
    teamsOptional: "Équipes (Optionnel)",
    enterEmail: "Entrez l'adresse email",
    enterCode: "Entrez le code",
    enterMobilePhone: "Entrez le téléphone portable",
    selectTeams: "Sélectionnez les équipes",
    selectTeam: "Sélectionnez l'équipe",
    allTeams: "Toutes les équipes",
    addGuard: "Ajouter un gardien",
    adding: "Ajout...",
    
    // Schedule and Alerts
    scheduleAlerts: "Alertes de planning",
    aboutScheduleAlerts: "À propos des alertes de planning",
    scheduleAlertsDescription: "Description des alertes de planning",
    customDateGuardView: "Vue de gardien personnalisée par date",
    allGuardsMonthView: "Vue mensuelle de tous les gardiens",
    allGuardsWeekView: "Vue hebdomadaire de tous les gardiens",
    customEmployeeReport: "Rapport d'employé personnalisé",
    scheduleDescription: "Description du planning",
    guardWorkingHoursForTeam: "Heures de travail du gardien pour l'équipe",
    
    // Shift Management
    deleteShift: "Supprimer l'équipe",
    deleteShiftConfirmation: "Êtes-vous sûr de vouloir supprimer cette équipe ? Cette action ne peut pas être annulée.",
    deleting: "Suppression...",
    assignedGuards: "gardiens assignés",
    noGuardsAssigned: "Aucun gardien assigné",
    location: "Emplacement",
    guards: "Gardiens",
    eventDeleted: "Événement supprimé",
    errorDeletingEvent: "Erreur lors de la suppression de l'événement",
    
    // View Modes
    dayView: "Jour",
    weekView: "Semaine",
    monthView: "Mois",
    customView: "Personnalisé",
    totalWorkView: "Travail total",
    
    // Alert Types
    allAlerts: "Toutes les alertes",
    insufficientHours: "Heures insuffisantes",
    unassignedSchedule: "Planning non assigné",
    nightMorningShift: "Équipe de nuit-matin",
    insufficientRest: "Repos insuffisant",
    visibilityStatus: "Statut de visibilité",
    missingTemplateShifts: "Équipes de modèle manquantes",
    filterByAlertType: "Filtrer par type d'alerte",
    selectAlertType: "Sélectionnez le type d'alerte",
    
    // HR Analytics
    hiringAnalytics: "Analytiques de Recrutement",
    selectRange: "Sélectionner la plage",
    last30Days: "Derniers 30 jours",
    last90Days: "Derniers 90 jours",
    last12Months: "12 mois",
    totalInterviews: "Total des Entretiens",
    totalInterviewsCompleted: "Total des Entretiens (terminés)",
    range: "Plage",
    days: "jours",
    
    // Candidate List
    searchCandidates: "Rechercher des candidats...",
    filterByMunicipality: "Filtrer par municipalité...",
    allRegions: "Toutes les Régions",
    centralSector: "Secteur Central",
    north: "Nord",
    south: "Sud",
    west: "Ouest",
    east: "Est",
    piraeus: "Pirée",
    region: "Région",
    manage: "Gérer",
    testResults: "Résultats",
    reconsider: "Reconsidérer",
    deleteCandidate: "Supprimer le Candidat",
    deleteCandidateConfirmation: "Êtes-vous sûr de vouloir supprimer {name}? Cette action ne peut pas être annulée.",
    noCandidatesFound: "Aucun candidat trouvé",
    candidateDeletedSuccess: "Candidat et toutes les données associées supprimés avec succès",
    deleted: "Supprimé",
    loadingCandidates: "Chargement des candidats...",
    application: "Candidature",
    
    // Provider Evaluation
    providerEvaluations: "Évaluations du Fournisseur",
    providerEvaluationsDesc: "Soumettre et gérer les évaluations du fournisseur",
    filterByClient: "Filtrer par Client",
    allClients: "Tous les Clients",
    analytics: "Analytiques",
    mitigations: "Atténuations",
    newEvaluation: "Nouvelle Évaluation",
    history: "Historique",
    customerEvaluationAnalytics: "Analytiques d'Évaluation Client",
    fullOverviewAndAnalysis: "Vue d'ensemble et analyse complète des évaluations du prestataire",
    overallRating: "Note Globale",
    averageOfAllMetrics: "Moyenne de toutes les métriques",
    wouldRecommend: "Recommanderaient",
    clientsWhoWouldRecommend: "Clients qui recommanderaient",
    wouldRenew: "Renouvelleraient",
    clientsWhoWouldRenew: "Clients qui renouvelleraient",
    clientsLowercase: "clients",
    serviceQuality: "Qualité de Service",
    technologyAndInnovation: "Technologie & Innovation",
    valueAndRelationship: "Valeur & Relation",
    allEvaluations: "Toutes les Évaluations",
    whatWorksWell: "Ce qui Fonctionne Bien",
    npsScoreBreakdown: "Répartition du Net Promoter Score",
    responseTime: "Temps de Réponse",
    incidentHandling: "Gestion des Incidents",
    guardQuality: "Qualité des Gardiens",
    professionalism: "Professionnalisme",
    communication: "Communication",
    technologySatisfaction: "Satisfaction Technologique",
    reportingQuality: "Qualité des Rapports",
    innovation: "Innovation",
    valueForMoney: "Rapport Qualité-Prix",
    contractFlexibility: "Flexibilité du Contrat",
    accountManagement: "Gestion de Compte",
    moduleNotAvailable: "Module Non Disponible",
    providerEvaluationsModuleDisabled: "Le module d'Évaluations du Fournisseur n'est pas activé pour votre compte. Veuillez contacter votre administrateur.",
    
    // Common Actions
    view: "Voir",
    edit: "Modifier",
    delete: "Supprimer",
    add: "Ajouter",
    create: "Créer",
    save: "Enregistrer",
    cancel: "Annuler",
    submit: "Soumettre",
    filter: "Filtrer",
    search: "Rechercher",
    back: "Retour",
    backTo: "Retour à",
    next: "Suivant",
    previous: "Précédent",
    close: "Fermer",
    open: "Ouvrir",
    refresh: "Actualiser",
    download: "Télécharger",
    upload: "Téléverser",
    export: "Exporter",
    import: "Importer",
    print: "Imprimer",
    
    // Common Status
    active: "Actif",
    inactive: "Inactif",
    pending: "En attente",
    approved: "Approuvé",
    rejected: "Rejeté",
    loadingData: "Chargement des données...",
    
    // Common Labels
    hours: "Heures",
    total: "Total",
    selectAll: "Tout sélectionner",
    deselectAll: "Tout désélectionner",
    selected: "Sélectionné",
    notes: "Notes",
    comments: "Commentaires",
    attachments: "Pièces jointes",
    
    // Time Greetings
    goodMorning: "Bonjour",
    goodAfternoon: "Bon après-midi",
    goodEvening: "Bonsoir",
    
    // Dashboard Items
    enhancedDashboard: "Tableau de bord amélioré",
    enhancedDashboardDesc: "Gestion avancée de la sécurité et du système",
    hrManagementDesc: "Gérer les candidats, entretiens et processus RH",
    leaveRequestsDesc: "Soumettre et gérer les demandes de congé",
    facilityPresence: "Présence des installations",
    facilityPresenceDesc: "Surveiller la présence et l'occupation des installations",
    applicationDocumentation: "Documentation de l'application",
    applicationDocumentationDesc: "Voir la documentation complète du système",
    patrolMonitoringSystem: "Système de surveillance des patrouilles",
    manageAdministrativeTeam: "Gérer l'équipe administrative",
    
    // Error Messages
    errorLoadingData: "Erreur de chargement des données",
    errorSavingData: "Erreur d'enregistrement des données",
    errorDeletingData: "Erreur de suppression des données",
    errorUpdatingData: "Erreur de mise à jour des données",
    somethingWentWrong: "Quelque chose s'est mal passé",
    tryAgainLater: "Veuillez réessayer plus tard",
    
    // Success Messages
    savedSuccessfully: "Enregistré avec succès",
    deletedSuccessfully: "Supprimé avec succès",
    updatedSuccessfully: "Mis à jour avec succès",
    createdSuccessfully: "Créé avec succès",
    
    // Form Validation
    required: "Requis",
    invalidEmail: "Email invalide",
    invalidPhone: "Numéro de téléphone invalide",
    invalidDate: "Date invalide",
    fieldRequired: "Ce champ est requis",
    pleaseEnter: "Veuillez saisir",
    pleaseSelect: "Veuillez sélectionner",
    
    // Pagination
    page: "Page",
    of: "de",
    showing: "Affichage",
    to: "à",
    results: "résultats",
    noResults: "Aucun résultat trouvé",
    noData: "Aucune donnée disponible",
    
    // Performance Management
    performanceManagement: "Gestion des évaluations du personnel",
    newBaseScoreSystem: "Nouveau système de score de base - Vue avancée et gestion des évaluations",
    backToEvaluations: "Retour aux évaluations",
    totalEmployees: "Total des employés",
    activeEmployees: "Employés actifs",
    evaluations: "Évaluations",
    newSystem: "Nouveau système",
    average: "Moyenne",
    averageScoreNewSystem: "Score moyen (nouveau système)",
    topPerformers: "Meilleurs performeurs",
    aPlusAndAGrades: "Notes A+ et A",
    selectEmployee: "Sélectionner un employé",
    selectEmployeeToViewEvaluations: "Sélectionnez un employé pour voir les évaluations",
    newBaseScoreSystemDetailed: "Nouveau système de score de base - Vue détaillée des performances",
    selectEmployeeFromList: "Sélectionnez un employé dans la liste pour voir ses évaluations",
    refreshData: "Actualiser les données",
    behaviors: "Comportements",
    successGuide: "Guide de réussite",
    
    // Equipment Management
    suppliers: "Fournisseurs",
    supplierManagement: "Gestion des Fournisseurs",
    addSupplier: "Ajouter Fournisseur",
    editSupplier: "Modifier Fournisseur",
    supplierName: "Nom du Fournisseur",
    registrationNumber: "Numéro d'Enregistrement",
    website: "Site Web",
    evaluationDate: "Date d'Évaluation",
    qualityScore: "Score Qualité",
    deliveryPerformance: "Performance de Livraison",
    pricingCompetitiveness: "Compétitivité des Prix",
    complianceCertification: "Conformité et Certification",
    communicationResponsiveness: "Communication et Réactivité",
    technicalSupport: "Support Technique",
    improvementActions: "Actions d'Amélioration",
    companyAssets: "Actifs de l'Entreprise",
    assetManagement: "Gestion des Actifs",
    addAsset: "Ajouter Actif",
    editAsset: "Modifier Actif",
    assetName: "Nom de l'Actif",
    assetCategory: "Catégorie d'Actif",
    purchasePrice: "Prix d'Achat",
    currentValue: "Valeur Actuelle",
    depreciationRate: "Taux d'Amortissement (%)",
    maintenanceSchedule: "Programme de Maintenance",
    lastMaintenanceDate: "Dernière Maintenance",
    nextMaintenanceDate: "Prochaine Maintenance",
    excellent: "Excellent",
    fair: "Moyen",
    poor: "Mauvais",
    itEquipment: "Équipement IT",
    itEquipmentManagement: "Gestion Équipement IT",
    equipmentName: "Nom de l'Équipement",
    equipmentType: "Type d'Équipement",
    model: "Modèle",
    licenseKey: "Clé de Licence",
    warrantyExpiry: "Expiration Garantie",
    assignedTo: "Assigné À",
    ipAddress: "Adresse IP",
    macAddress: "Adresse MAC",
    specifications: "Spécifications",
    maintenanceNotes: "Notes de Maintenance",
    server: "Serveur",
    workstation: "Station de Travail",
    laptop: "Ordinateur Portable",
    network: "Équipement Réseau",
    software: "Logiciel",
    guardEquipmentList: "Liste Équipement Gardes",
    teamEquipmentList: "Liste Équipement Équipes",
    guardsEquipment: "Équipement Assigné aux Gardes",
    teamsEquipment: "Équipement Assigné aux Équipes",
    totalEquipmentValue: "Valeur Totale Équipement",
    unitPrice: "Prix Unitaire",
    totalPrice: "Prix Total",
    supplier: "Fournisseur",
    selectSupplier: "Sélectionner Fournisseur",
    ethicsCompliance: "Éthique & Conformité",
    companyPolicies: "Politiques de l'Entreprise",
    codeOfEthics: "Code de Déontologie",
    codeOfConduct: "Code de Conduite",
    gdprPolicy: "Politique RGPD",
    uploadPolicy: "Télécharger la Politique",
    policyAcknowledgement: "Reconnaissance de Politique",
    acknowledgePolicies: "Reconnaître les Politiques",
    iHaveReadAndAgree: "J'ai lu et j'accepte de me conformer à",
    acceptAndAcknowledge: "Accepter & Reconnaître",
    mustAcknowledgePolicies: "Vous devez reconnaître toutes les politiques pour continuer",
    policyUploadedSuccessfully: "Politique téléchargée avec succès",
    acknowledgedUsers: "Utilisateurs Ayant Reconnu",
    acknowledged: "Reconnu",
    pendingUsers: "Utilisateurs en Attente",
    acknowledgementRate: "Taux de Reconnaissance",
    viewPolicyStats: "Voir les Statistiques",
    policyStatistics: "Statistiques de Reconnaissance des Politiques",
    totalUsers: "Total des Utilisateurs",
    noActivePolicy: "Aucune politique active disponible",
    uploadNewVersion: "Télécharger une Nouvelle Version",
    customTabName: "Nom d'Onglet Personnalisé",
    optional: "Optionnel",
    customTabNameHelp: "Personnalisez le nom de l'onglet Éthique & Conformité dans le tableau de bord",
    uploadNewVersionNote: "Le téléchargement d'une nouvelle version nécessitera que les utilisateurs acceptent à nouveau",
    moduleDisabledMessage: "Ce module est actuellement désactivé pour votre organisation. Veuillez contacter votre administrateur pour l'activer.",
    disabled: "Désactivé",
    moduleManagement: "Gestion des Modules",
    moduleManagementDesc: "Activer ou désactiver les modules du système",
    enableDisableModules: "Activer ou désactiver les modules pour votre organisation",
    moduleManagementInfo: "Contrôlez les fonctionnalités disponibles pour votre organisation. La désactivation d'un module le cachera de tous les utilisateurs.",
    ethicsComplianceModuleDesc: "Politiques d'entreprise, code de déontologie et gestion de la conformité",
    equipmentManagementModuleDesc: "Types d'équipement, affectations et suivi des stocks",
    supplierEvaluationsModuleDesc: "Gestion des fournisseurs et évaluations ISO 9001",
    currentVersion: "Version Actuelle",
    policyType: "Type de Politique",
    viewPolicy: "Voir la Politique",
    downloadPolicy: "Télécharger la Politique",
    policiesRequireAcknowledgement: "Les Politiques de l'Entreprise Nécessitent Votre Reconnaissance",
    pleaseReviewPolicies: "Veuillez examiner et reconnaître les politiques suivantes de l'entreprise avant de continuer. Ces politiques sont essentielles pour maintenir les normes éthiques et la conformité.",
    iPledgeToComply: "Je m'engage à lire, comprendre et me conformer à toutes les politiques de l'entreprise",
    noSupplier: "Aucun Fournisseur",
    quarter1: "T1",
    quarter2: "T2",
    quarter3: "T3",
    quarter4: "T4",
    annual: "Annuel",
    vehicle: "Véhicule",
    building: "Bâtiment",
    furniture: "Mobilier",
    criteria: "Critères",
    selectUser: "Sélectionner Utilisateur",
    maintenance: "Maintenance",
    retired: "Retiré",
    viewEvaluation: "Voir Évaluation",
    securityInventory: "Inventaire de Sécurité",
    recordSupplierStrengths: "Enregistrez les points forts du fournisseur...",
    recordSupplierWeaknesses: "Enregistrez les faiblesses du fournisseur...",
    suggestImprovements: "Suggestions pour améliorer la collaboration...",
    supplierHasIsoCertification: "Le fournisseur possède une certification ISO",
    evaluationStatus: "État de l'Évaluation",
    nextEvaluationDate: "Prochaine Évaluation",
    saveEvaluation: "Enregistrer l'Évaluation",
    supplierEvaluation: "Évaluation Fournisseur",
  },
};

// Language Provider component
interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Set default language to Greek instead of English
  const [locale, setLocale] = React.useState<string>(localStorage.getItem('locale') || 'el');

  React.useEffect(() => {
    localStorage.setItem('locale', locale);
  }, [locale]);

  const t = (key: string, args?: Record<string, any>): string => {
    let translation = translations[locale as keyof typeof translations]?.[key];
    
    // If translation is not found, return undefined so fallback text can be used
    if (!translation) {
      return undefined as any;
    }

    if (args) {
      Object.keys(args).forEach(argKey => {
        const regex = new RegExp(`{${argKey}}`, 'g');
        translation = translation.replace(regex, args[argKey]);
      });
    }

    return translation;
  };

  // Provide language as an alias for locale and setLanguage as an alias for setLocale
  return (
    <LanguageContext.Provider value={{ 
      locale, 
      language: locale, 
      t, 
      setLocale, 
      setLanguage: setLocale 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
